import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  GitBranch,
  PlayCircle,
  PauseCircle,
  XCircle,
  RotateCcw,
  Calendar,
  CheckCheck,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, useToast } from '@caruso/ui';
import { flowsService } from '@/services/flows.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function ExecutionDetails() {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: execution, isLoading } = useQuery({
    queryKey: ['execution', executionId],
    queryFn: () => flowsService.getExecutionById(executionId!),
    enabled: !!executionId,
  });

  const pauseMutation = useMutation({
    mutationFn: () => flowsService.pauseExecution(executionId!),
    onSuccess: () => {
      toast({ title: 'Execução pausada', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId] });
    },
    onError: () => {
      toast({ title: 'Erro ao pausar execução', variant: 'destructive' });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => flowsService.resumeExecution(executionId!),
    onSuccess: () => {
      toast({ title: 'Execução retomada', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId] });
    },
    onError: () => {
      toast({ title: 'Erro ao retomar execução', variant: 'destructive' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => flowsService.cancelExecution(executionId!),
    onSuccess: () => {
      toast({ title: 'Execução cancelada', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId] });
    },
    onError: () => {
      toast({ title: 'Erro ao cancelar execução', variant: 'destructive' });
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => flowsService.retryExecution(executionId!),
    onSuccess: () => {
      toast({
        title: 'Execução reiniciada',
        description: 'A execução será processada novamente em breve',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId] });
    },
    onError: () => {
      toast({ title: 'Erro ao reiniciar execução', variant: 'destructive' });
    },
  });

  const executeAgainMutation = useMutation({
    mutationFn: () => flowsService.startFlow(execution?.flowId!, { clientIds: [execution?.clientId!] }),
    onSuccess: (data) => {
      toast({
        title: 'Nova execução criada',
        description: 'O fluxo será executado novamente para este cliente',
        variant: 'success',
      });
      // Navega para a nova execução se houver
      if (data.executions && data.executions.length > 0) {
        navigate(`/app/fluxos/execucoes/${data.executions[0].id}`);
      }
    },
    onError: () => {
      toast({ title: 'Erro ao executar fluxo novamente', variant: 'destructive' });
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { className: string; icon: any; label: string }> = {
      pending: {
        className: 'bg-brand-ivory/10 text-brand-ivory/70 border-brand-ivory/20',
        icon: Clock,
        label: 'Pendente',
      },
      running: {
        className: 'bg-brand-gold/10 text-brand-gold border-brand-gold/20',
        icon: PlayCircle,
        label: 'Em andamento',
      },
      completed: {
        className: 'bg-green-500/10 text-green-400 border-green-500/20',
        icon: CheckCircle,
        label: 'Concluído',
      },
      failed: {
        className: 'bg-red-500/10 text-red-400 border-red-500/20',
        icon: AlertCircle,
        label: 'Falhou',
      },
      paused: {
        className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
        icon: PauseCircle,
        label: 'Pausado',
      },
      waiting_response: {
        className: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        icon: MessageSquare,
        label: 'Aguardando resposta',
      },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium border ${config.className}`}
      >
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const formatDateTime = (date: string | undefined) => {
    if (!date) return '-';
    return format(new Date(date), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
  };

  // Formata texto no estilo WhatsApp: *negrito*, _itálico_, ~riscado~, ```monoespaçado```
  const formatWhatsAppText = (text: string) => {
    if (!text) return null;

    const parts: React.ReactNode[] = [];
    let key = 0;

    // Combina todos os padrões em um único regex para processar na ordem correta
    // Ordem importa: ```code``` primeiro (mais específico), depois os outros
    const combinedRegex = /(```[^`]+```|\*[^*]+\*|_[^_]+_|~[^~]+~)/g;

    let lastIndex = 0;
    let match;

    while ((match = combinedRegex.exec(text)) !== null) {
      // Adiciona texto antes do match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      const matchedText = match[0];

      // Determina qual formatação aplicar
      if (matchedText.startsWith('```') && matchedText.endsWith('```')) {
        const content = matchedText.slice(3, -3);
        parts.push(<code key={key++} className="bg-brand-ink/50 px-1.5 py-0.5 rounded font-mono text-sm">{content}</code>);
      } else if (matchedText.startsWith('*') && matchedText.endsWith('*')) {
        const content = matchedText.slice(1, -1);
        parts.push(<strong key={key++} className="font-bold">{content}</strong>);
      } else if (matchedText.startsWith('_') && matchedText.endsWith('_')) {
        const content = matchedText.slice(1, -1);
        parts.push(<em key={key++} className="italic">{content}</em>);
      } else if (matchedText.startsWith('~') && matchedText.endsWith('~')) {
        const content = matchedText.slice(1, -1);
        parts.push(<span key={key++} className="line-through">{content}</span>);
      }

      lastIndex = match.index + matchedText.length;
    }

    // Adiciona texto restante
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');

    // Brazilian numbers with country code
    if (digits.startsWith('55') && digits.length >= 12) {
      const ddd = digits.slice(2, 4);
      const rest = digits.slice(4);
      if (rest.length === 9) {
        return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
      } else if (rest.length === 8) {
        return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
      }
    }

    // Brazilian without country code
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    // International format
    if (digits.length > 10) {
      return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10, -6)} ${digits.slice(-6)}`;
    }

    return phone;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-white">Carregando...</p>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-white">Execução não encontrada</p>
      </div>
    );
  }

  let executionData: any = { variables: {}, responses: {}, currentPath: [] };
  try {
    if (execution.executionData) {
      executionData = JSON.parse(execution.executionData);
    }
  } catch (e) {
    console.error('Error parsing execution data:', e);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/fluxos/execucoes')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-white">Detalhes da Execução</h1>
            <p className="text-brand-ivory/80 mt-1 text-lg">{execution.flow?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(execution.status)}
          {execution.status === 'running' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
            >
              <PauseCircle className="w-4 h-4 mr-2" />
              Pausar
            </Button>
          )}
          {execution.status === 'paused' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Retomar
            </Button>
          )}
          {(execution.status === 'running' || execution.status === 'paused') && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
          )}
          {execution.status === 'failed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
              className="text-brand-gold hover:bg-brand-gold/10"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Tentar Novamente
            </Button>
          )}
          <Button
            variant="premium"
            size="sm"
            onClick={() => executeAgainMutation.mutate()}
            disabled={executeAgainMutation.isPending}
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            {executeAgainMutation.isPending ? 'Executando...' : 'Executar Novamente'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações do Cliente */}
        <Card className="bg-brand-smoke border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold flex items-center gap-2">
              <User className="w-5 h-5" />
              Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-brand-ivory/60 text-sm">Nome</p>
              {execution.clientId ? (
                <a
                  href={`/app/clientes/${execution.clientId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gold hover:underline font-medium"
                >
                  {execution.client?.name || 'Sem nome'}
                </a>
              ) : (
                <p className="text-white font-medium">{execution.client?.name || 'Sem nome'}</p>
              )}
            </div>
            {execution.client?.phone && (
              <div>
                <p className="text-brand-ivory/60 text-sm">Telefone</p>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-brand-gold" />
                  <a
                    href={`https://wa.me/${execution.client.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-gold hover:underline"
                  >
                    {formatPhone(execution.client.phone)}
                  </a>
                </div>
              </div>
            )}
            {execution.client?.email && (
              <div>
                <p className="text-brand-ivory/60 text-sm">Email</p>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-brand-gold" />
                  <a
                    href={`mailto:${execution.client.email}`}
                    className="text-brand-gold hover:underline"
                  >
                    {execution.client.email}
                  </a>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Informações da Execução */}
        <Card className="bg-brand-smoke border-brand-gold/20 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-brand-gold flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Informações da Execução
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-brand-ivory/60 text-sm">Criado em</p>
                <div className="flex items-center gap-2 text-white text-sm">
                  <Calendar className="w-4 h-4 text-brand-gold" />
                  <p>{formatDateTime(execution.createdAt)}</p>
                </div>
              </div>
              <div>
                <p className="text-brand-ivory/60 text-sm">Iniciado em</p>
                <div className="flex items-center gap-2 text-white text-sm">
                  <PlayCircle className="w-4 h-4 text-brand-gold" />
                  <p>{formatDateTime(execution.startedAt)}</p>
                </div>
              </div>
              <div>
                <p className="text-brand-ivory/60 text-sm">Finalizado em</p>
                <div className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle className="w-4 h-4 text-brand-gold" />
                  <p>{formatDateTime(execution.completedAt)}</p>
                </div>
              </div>
              <div>
                <p className="text-brand-ivory/60 text-sm">Nó Atual</p>
                <p className="text-white text-sm font-mono">
                  {execution.currentNodeId || 'Nenhum'}
                </p>
              </div>
              <div>
                <p className="text-brand-ivory/60 text-sm">Nós Visitados</p>
                <p className="text-white text-sm">{executionData.currentPath?.length || 0}</p>
              </div>
              <div>
                <p className="text-brand-ivory/60 text-sm">Mensagens</p>
                <p className="text-white text-sm">{execution.messages?.length || 0}</p>
              </div>
            </div>

            {execution.error && (
              <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <strong>Erro:</strong> {execution.error}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conversa */}
      {execution.messages && execution.messages.length > 0 && (
        <Card className="bg-brand-smoke border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Conversa ({execution.messages.length} mensagens)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {execution.messages.map((message, idx) => {
                const isInbound = message.direction === 'inbound';
                return (
                  <div
                    key={message.id}
                    className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        isInbound
                          ? 'bg-brand-ivory/10 border border-brand-ivory/20'
                          : 'bg-brand-gold/10 border border-brand-gold/20'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {isInbound ? (
                          <User className="w-4 h-4 text-brand-ivory/60" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-brand-gold" />
                        )}
                        <span className={`text-sm font-medium ${isInbound ? 'text-brand-ivory/80' : 'text-brand-gold'}`}>
                          {isInbound ? 'Cliente' : 'Sistema'}
                        </span>
                        {!isInbound && message.nodeId && (
                          <span className="text-brand-ivory/40 text-xs">
                            Nó: {message.nodeId}
                          </span>
                        )}
                      </div>
                      <p className="text-white whitespace-pre-wrap">{formatWhatsAppText(message.content)}</p>
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-brand-gold/10">
                        <p className="text-brand-ivory/50 text-xs">
                          {formatDateTime(message.sentAt)}
                        </p>
                        <div className="flex items-center gap-2">
                          {!isInbound && message.delivered && (
                            <span className="text-green-400 flex items-center gap-1 text-xs">
                              <CheckCheck className="w-3 h-3" />
                              Entregue
                            </span>
                          )}
                          {!isInbound && message.read && (
                            <span className="text-blue-400 flex items-center gap-1 text-xs">
                              <Eye className="w-3 h-3" />
                              Lida
                            </span>
                          )}
                          {message.error && (
                            <span className="text-red-400 flex items-center gap-1 text-xs">
                              <AlertCircle className="w-3 h-3" />
                              {message.error}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Caminho Percorrido */}
      {executionData.currentPath && executionData.currentPath.length > 0 && (
        <Card className="bg-brand-smoke border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Caminho Percorrido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {executionData.currentPath.map((nodeId: string, idx: number) => (
                <div
                  key={idx}
                  className="bg-brand-ink/30 border border-brand-gold/20 rounded-lg px-3 py-2 text-sm"
                >
                  <span className="text-brand-gold font-medium">{idx + 1}.</span>{' '}
                  <span className="text-white font-mono">{nodeId}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Variáveis e Respostas */}
      {(Object.keys(executionData.variables || {}).length > 0 ||
        Object.keys(executionData.responses || {}).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.keys(executionData.variables || {}).length > 0 && (
            <Card className="bg-brand-smoke border-brand-gold/20">
              <CardHeader>
                <CardTitle className="text-brand-gold text-lg">Variáveis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(executionData.variables).map(([key, value]) => (
                    <div
                      key={key}
                      className="bg-brand-ink/30 border border-brand-gold/10 rounded-lg p-3"
                    >
                      <p className="text-brand-gold text-sm font-mono">{key}</p>
                      <p className="text-white mt-1">{JSON.stringify(value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {Object.keys(executionData.responses || {}).length > 0 && (
            <Card className="bg-brand-smoke border-brand-gold/20">
              <CardHeader>
                <CardTitle className="text-brand-gold text-lg">Respostas do Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(executionData.responses).map(([nodeId, response]) => (
                    <div
                      key={nodeId}
                      className="bg-brand-ink/30 border border-brand-gold/10 rounded-lg p-3"
                    >
                      <p className="text-brand-gold text-sm font-mono">{nodeId}</p>
                      <p className="text-white mt-1">{String(response)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
